import React, { useEffect, useState, useCallback } from 'react';
import { ShieldCheck, Trash2, Plus, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { getAuthHeaders } from '../utils/auth';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const GoogleAccessManager = () => {
  const [allowed, setAllowed] = useState(null);
  const [email, setEmail] = useState('');
  const [linkToMe, setLinkToMe] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/auth/allowlist`, { headers: getAuthHeaders() });
      setAllowed(r.data.allowed || []);
    } catch { setAllowed([]); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!email.includes('@')) { toast.error('Enter a valid email'); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/auth/allowlist`,
        { email, link_to_me: linkToMe }, { headers: getAuthHeaders() });
      toast.success(`${email} can now sign in with Google${linkToMe ? ' — into YOUR account' : ''}`);
      setEmail('');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not add'); }
    setBusy(false);
  };

  const remove = async (em) => {
    try {
      await axios.delete(`${API}/auth/allowlist/${encodeURIComponent(em)}`, { headers: getAuthHeaders() });
      toast.success(`${em} removed — their sessions are revoked`);
      load();
    } catch { toast.error('Could not remove'); }
  };

  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5" data-testid="google-access-section">
      <div className="flex items-center gap-2 mb-1.5">
        <ShieldCheck className="w-4.5 h-4.5 text-emerald-400" />
        <h3 className="text-[15px] font-bold text-white">Google Sign-in Access</h3>
      </div>
      <p className="text-[12px] text-zinc-500 mb-4">
        LEE is private. Only emails on this list can sign in with Google — everyone else is refused.
        Check "into my account" for YOUR OWN Gmail so it opens your data.
      </p>
      <div className="flex flex-col sm:flex-row gap-2 mb-2">
        <input value={email} onChange={(e) => setEmail(e.target.value)}
          placeholder="name@gmail.com"
          className="flex-1 bg-white/[0.04] border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white placeholder-zinc-600 outline-none focus:border-amber-500/40"
          data-testid="allowlist-email-input" />
        <button onClick={add} disabled={busy}
          className="px-5 py-2.5 rounded-lg bg-amber-500 text-black text-sm font-bold hover:bg-amber-400 disabled:opacity-50 flex items-center justify-center gap-1.5"
          data-testid="allowlist-add-btn">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Allow
        </button>
      </div>
      <label className="flex items-center gap-2 text-[12px] text-zinc-400 mb-4 cursor-pointer">
        <input type="checkbox" checked={linkToMe} onChange={(e) => setLinkToMe(e.target.checked)}
          className="accent-amber-500" data-testid="allowlist-link-checkbox" />
        Sign into my account (use for your own Gmail)
      </label>
      {allowed === null ? (
        <p className="text-xs text-zinc-600">Loading…</p>
      ) : allowed.length === 0 ? (
        <p className="text-xs text-zinc-600" data-testid="allowlist-empty">
          No one is allowed yet — Google sign-in is fully locked.
        </p>
      ) : (
        <div className="space-y-2" data-testid="allowlist-items">
          {allowed.map((a) => (
            <div key={a.email} className="flex items-center justify-between gap-2 rounded-lg bg-white/[0.02] border border-white/[0.06] px-3 py-2" data-testid="allowlist-item">
              <div className="min-w-0">
                <p className="text-[13px] text-white truncate">{a.email}</p>
                <p className="text-[11px] text-zinc-600">{a.link_user_id ? 'Opens your account' : 'Own separate account'}</p>
              </div>
              <button onClick={() => remove(a.email)}
                className="text-zinc-600 hover:text-red-400 p-1 shrink-0"
                data-testid="allowlist-remove-btn" title="Revoke access">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
